import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { 
  FileText, 
  Calendar, 
  Building, 
  User, 
  DollarSign, 
  Hash,
  AlertCircle,
  CheckCircle,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Package,
  ThumbsUp,
  ThumbsDown,
  Edit,
  MessageCircle,
  Bot,
  XCircle
} from 'lucide-react';
import './InvoiceDetail.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

const InvoiceDetail = ({ invoiceId, onNavigate, currentIndex, totalCount }) => {
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [comment, setComment] = useState('');
  const [showCommentBox, setShowCommentBox] = useState(false);

  useEffect(() => {
    fetchInvoiceDetail();
  }, [invoiceId]);

  const fetchInvoiceDetail = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE}/api/invoices/${invoiceId}`);
      setInvoice(response.data);
    } catch (err) {
      setError('Failed to load invoice details');
      console.error('Error fetching invoice detail:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApproval = async (action) => {
    try {
      const comments = comment.trim() ? [comment] : [];
      const payload = {
        approved_by: 'user',
        comments: comments
      };

      const endpoint = action === 'approve' ? 'approve' : 'reject';
      await axios.post(`${API_BASE}/api/invoices/${invoiceId}/${endpoint}`, payload);
      
      // Refresh invoice data
      await fetchInvoiceDetail();
      setComment('');
      setShowCommentBox(false);
    } catch (err) {
      console.error(`Error ${action}ing invoice:`, err);
      setError(`Failed to ${action} invoice`);
    }
  };

  const formatCurrency = (amount, currency = 'EUR') => {
    return new Intl.NumberFormat('hr-HR', {
      style: 'currency',
      currency: currency
    }).format(amount || 0);
  };

  const getApprovalIcon = (status) => {
    switch (status) {
      case 'approved': return <CheckCircle className="status-icon approved" />;
      case 'rejected': return <XCircle className="status-icon rejected" />;
      case 'needs_review': return <AlertTriangle className="status-icon warning" />;
      default: return <AlertCircle className="status-icon pending" />;
    }
  };

  const getAIApprovalIcon = (status) => {
    switch (status) {
      case 'confident': return <Bot className="status-icon confident" />;
      case 'uncertain': return <Bot className="status-icon uncertain" />;
      case 'flagged': return <Bot className="status-icon flagged" />;
      default: return <Bot className="status-icon pending" />;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'valid':
        return <CheckCircle className="status-icon valid" size={20} />;
      case 'warning':
        return <AlertTriangle className="status-icon warning" size={20} />;
      case 'error':
        return <AlertCircle className="status-icon error" size={20} />;
      default:
        return <AlertCircle className="status-icon unchecked" size={20} />;
    }
  };

  if (loading) return <div className="loading">Loading invoice details...</div>;
  if (error) return <div className="error">{error}</div>;
  if (!invoice) return null;

  return (
    <div className="invoice-detail">
      <div className="detail-header">
        <div className="navigation-controls">
          <button 
            className="nav-button"
            onClick={() => onNavigate(-1)}
            disabled={currentIndex === 0}
            title="Previous invoice (← or P)"
          >
            <ChevronLeft size={20} />
          </button>
          <span className="nav-info">
            {currentIndex + 1} / {totalCount}
          </span>
          <button 
            className="nav-button"
            onClick={() => onNavigate(1)}
            disabled={currentIndex >= totalCount - 1}
            title="Next invoice (→ or N)"
          >
            <ChevronRight size={20} />
          </button>
        </div>
        
        <div className="status-header">
          {getStatusIcon(invoice.validation_status)}
          <span className={`status-text status-${invoice.validation_status}`}>
            {invoice.validation_status}
          </span>
        </div>
      </div>

      <div className="detail-content">
        <section className="detail-section">
          <h3>Invoice Information</h3>
          
          <div className="detail-row">
            <Hash size={16} />
            <span className="label">Invoice Number:</span>
            <span className="value">{invoice.invoice_number || 'N/A'}</span>
          </div>
          
          <div className="detail-row">
            <Calendar size={16} />
            <span className="label">Date:</span>
            <span className="value">
              {invoice.invoice_date 
                ? format(new Date(invoice.invoice_date), 'dd.MM.yyyy')
                : 'N/A'}
            </span>
          </div>
          
          <div className="detail-row">
            <FileText size={16} />
            <span className="label">Category:</span>
            <span className="value">{invoice.category || 'N/A'}</span>
          </div>
        </section>

        <section className="detail-section">
          <h3>Parties</h3>
          
          <div className="party-info">
            <h4><Building size={16} /> Supplier</h4>
            <p className="party-name">{invoice.supplier_name || 'Unknown'}</p>
            {invoice.supplier_oib && (
              <p className="party-detail">OIB: {invoice.supplier_oib}</p>
            )}
            {invoice.supplier_address && (
              <p className="party-detail">{invoice.supplier_address}</p>
            )}
          </div>
          
          <div className="party-info">
            <h4><User size={16} /> Customer</h4>
            <p className="party-name">{invoice.customer_name || 'Unknown'}</p>
            {invoice.customer_oib && (
              <p className="party-detail">OIB: {invoice.customer_oib}</p>
            )}
            {invoice.customer_address && (
              <p className="party-detail">{invoice.customer_address}</p>
            )}
          </div>
        </section>

        <section className="detail-section">
          <h3>Financial Summary</h3>
          
          <div className="financial-grid">
            <div className="financial-item">
              <span className="label">Subtotal:</span>
              <span className="value">{formatCurrency(invoice.subtotal, invoice.currency)}</span>
            </div>
            <div className="financial-item">
              <span className="label">Tax:</span>
              <span className="value">{formatCurrency(invoice.tax, invoice.currency)}</span>
            </div>
            <div className="financial-item total">
              <span className="label">Total:</span>
              <span className="value">{formatCurrency(invoice.total, invoice.currency)}</span>
            </div>
          </div>
          
          {invoice.tax && invoice.subtotal && (
            <div className="tax-rate">
              Tax Rate: {((invoice.tax / invoice.subtotal) * 100).toFixed(1)}%
            </div>
          )}
        </section>

        {invoice.line_items && invoice.line_items.length > 0 && (
          <section className="detail-section">
            <h3><Package size={16} /> Line Items ({invoice.line_items.length})</h3>
            
            <div className="line-items">
              {invoice.line_items.map((item, index) => (
                <div key={item.id || index} className="line-item">
                  <div className="item-description">{item.description}</div>
                  <div className="item-details">
                    <span>{item.qty} × {formatCurrency(item.unit_price)}</span>
                    <span className="item-total">{formatCurrency(item.total_price)}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {invoice.validation_notes && (
          <section className="detail-section validation-notes">
            <h3><AlertCircle size={16} /> Validation Notes</h3>
            <p>{invoice.validation_notes}</p>
          </section>
        )}

        {invoice.claude_analysis && (
          <section className="detail-section claude-analysis">
            <h3><Bot size={16} /> Claude Analysis</h3>
            <div className="analysis-content">
              {invoice.claude_analysis.split('\n').map((line, index) => (
                <p key={index}>{line}</p>
              ))}
            </div>
          </section>
        )}

        <section className="detail-section approval-section">
          <h3>Approval Status</h3>
          
          <div className="approval-grid">
            <div className="approval-item">
              <div className="approval-header">
                <User size={16} />
                <span>Manual Review</span>
              </div>
              <div className="approval-status">
                {getApprovalIcon(invoice.user_approval_status)}
                <span className={`status-text ${invoice.user_approval_status}`}>
                  {invoice.user_approval_status || 'pending'}
                </span>
              </div>
              {invoice.approved_by && (
                <div className="approval-meta">
                  By: {invoice.approved_by} {invoice.approved_at && 
                    `on ${format(new Date(invoice.approved_at), 'dd.MM.yyyy HH:mm')}`}
                </div>
              )}
            </div>

            <div className="approval-item">
              <div className="approval-header">
                <Bot size={16} />
                <span>AI Assessment</span>
              </div>
              <div className="approval-status">
                {getAIApprovalIcon(invoice.ai_approval_status)}
                <span className={`status-text ${invoice.ai_approval_status}`}>
                  {invoice.ai_approval_status || 'pending'}
                </span>
                {invoice.ai_confidence_score && (
                  <span className="confidence-score">
                    ({(invoice.ai_confidence_score * 100).toFixed(0)}%)
                  </span>
                )}
              </div>
            </div>
          </div>

          {invoice.user_comments && invoice.user_comments.length > 0 && (
            <div className="user-comments">
              <h4><MessageCircle size={14} /> Comments</h4>
              {invoice.user_comments.map((comment, index) => (
                <div key={index} className="comment">{comment}</div>
              ))}
            </div>
          )}

          {invoice.user_approval_status !== 'approved' && invoice.user_approval_status !== 'rejected' && (
            <div className="approval-actions">
              <button 
                className="comment-toggle-btn"
                onClick={() => setShowCommentBox(!showCommentBox)}
              >
                <MessageCircle size={16} />
                Add Comment
              </button>

              {showCommentBox && (
                <div className="comment-box">
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    onFocus={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    placeholder="Add your review comments..."
                    rows="3"
                  />
                </div>
              )}

              <div className="approval-buttons">
                <button 
                  className="approve-btn"
                  onClick={() => handleApproval('approve')}
                >
                  <ThumbsUp size={16} />
                  Approve
                </button>
                
                <button 
                  className="reject-btn"
                  onClick={() => handleApproval('reject')}
                >
                  <ThumbsDown size={16} />
                  Reject
                </button>
              </div>
            </div>
          )}
        </section>

        <section className="detail-section metadata">
          <h3>Metadata</h3>
          <div className="metadata-grid">
            <div>
              <span className="label">Confidence:</span>
              <span className="value">{(invoice.confidence * 100).toFixed(0)}%</span>
            </div>
            <div>
              <span className="label">Source:</span>
              <span className="value">{invoice.source}</span>
            </div>
            <div>
              <span className="label">Created:</span>
              <span className="value">
                {format(new Date(invoice.created_at), 'dd.MM.yyyy HH:mm')}
              </span>
            </div>
            {invoice.ocr_data && (
              <div>
                <span className="label">OCR Confidence:</span>
                <span className="value">
                  {invoice.ocr_data.confidence 
                    ? `${(invoice.ocr_data.confidence * 100).toFixed(0)}%`
                    : 'N/A'}
                </span>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default InvoiceDetail;